import { useContext, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { ImSpinner2 } from "react-icons/im";
import { HouseContext } from "../../context/HouseContext";
import { parseCoords } from "../../utils/propertyCoords";
import { getStatusClasses } from "../../utils/propertyStatusColors";

// Centro por defecto: Chascomús
const DEFAULT_CENTER = [-35.5706, -58.0098];

const isClosed = (h) => h.state === "Vendido" || h.state === "Alquilado";

const priceLabel = (h) => {
  if (h.currency && h.price)
    return `${h.currency} ${Number(h.price).toLocaleString("es-AR")}`;
  return "Consultar";
};

// Operación legible para mostrar en el popup ("Venta" / "Alquiler" / etc.)
const opLabel = (h) => h.state || "";

// Pin tipo "píldora de precio", con los MISMOS colores que el badge de las cards.
const makeIcon = (h) =>
  L.divIcon({
    className: "",
    html: `<div style="transform:translate(-50%,-100%)" class="inline-block px-2.5 py-1 rounded-full text-xs font-bold shadow-lg ring-2 ring-white whitespace-nowrap ${getStatusClasses(
      h.state
    )}">${priceLabel(h)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

// Ajusta el zoom para que entren todos los marcadores.
function FitBounds({ positions }) {
  const map = useMap();
  if (positions.length > 1) {
    map.fitBounds(positions, { padding: [50, 50] });
  } else if (positions.length === 1) {
    map.setView(positions[0], 15);
  }
  return null;
}

// Mapa reutilizable con todas las propiedades geolocalizadas.
// `heightClass` controla la altura (ej. "h-[78vh]" página completa, "h-[480px]" home).
function PropertiesMapView({ heightClass = "h-[78vh]" }) {
  const { houses, loading } = useContext(HouseContext);
  const [opFilter, setOpFilter] = useState(null); // null = todas | "Venta" | "Alquiler"

  // Solo propiedades disponibles con coordenadas (las vendidas/alquiladas no van al mapa).
  const points = useMemo(
    () =>
      (houses || [])
        .filter((h) => !isClosed(h))
        .map((h) => ({ h, pos: parseCoords(h.maps) }))
        .filter((p) => p.pos),
    [houses]
  );

  // Aplica el filtro de operación (botones Todas / Venta / Alquiler del mapa).
  const shown = opFilter ? points.filter((p) => p.h.state === opFilter) : points;

  const filters = [
    { key: null, label: "Todas", classes: "bg-white text-gray-700" },
    { key: "Venta", label: "Venta", classes: getStatusClasses("Venta") },
    { key: "Alquiler", label: "Alquiler", classes: getStatusClasses("Alquiler") },
  ];

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${heightClass} w-full`}>
        <ImSpinner2 className="animate-spin text-primary text-4xl" />
      </div>
    );
  }

  const positions = shown.map((p) => p.pos);
  const center = positions[0] || points[0]?.pos || DEFAULT_CENTER;

  return (
    <div className={`relative ${heightClass} w-full`}>
      {/* Filtros de operación (clickeables) */}
      <div className="absolute top-3 right-3 z-[1000] flex gap-2">
        {filters.map((f) => (
          <button
            key={f.label}
            type="button"
            onClick={() => setOpFilter(f.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full shadow-md transition ${
              f.classes
            } ${
              opFilter === f.key
                ? "ring-2 ring-offset-1 ring-primary"
                : "opacity-70 hover:opacity-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        className="h-full w-full z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          subdomains="abc"
          maxZoom={19}
        />
        <FitBounds positions={positions} />

        {shown.map(({ h, pos }) => (
          <Marker key={h.id} position={pos} icon={makeIcon(h)}>
            <Popup>
              <div className="w-56">
                {h.images && h.images[0] && (
                  <div className="relative mb-2">
                    <img
                      src={h.images[0]}
                      alt={h.address}
                      className="w-full h-28 object-cover rounded-lg"
                    />
                    {opLabel(h) && (
                      <span
                        className={`absolute top-2 left-2 text-sm font-bold px-3 py-1 rounded-full shadow ${getStatusClasses(
                          h.state
                        )}`}
                      >
                        {opLabel(h)}
                      </span>
                    )}
                  </div>
                )}
                <h3 className="text-sm font-bold text-gray-800 leading-tight">
                  {h.address}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {[h.type, h.country].filter(Boolean).join(" · ")}
                </p>
                <p className="text-base font-extrabold text-primary mt-1">
                  {priceLabel(h)}
                </p>
                <Link
                  to={`/propiedad/${h.id}`}
                  className="mt-2 inline-block w-full text-center bg-primary hover:bg-secondary !text-white no-underline hover:no-underline text-sm font-medium py-2 rounded-lg transition-colors"
                >
                  Ver propiedad
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

export default PropertiesMapView;
